import type {
  ApiChangeMyPasswordRequest,
  ApiErrorBody,
  ApiLoginResult,
  ApiUpdateMyProfileRequest,
  ApiUserProfile,
} from "@tm-food/api-contract";
import { getApiBaseUrl } from "@/app/config/env";
import { nativeHttpAvailable, requestNativeBlob } from "@/platform/nativeHttpBlob";
import { normalizeApiError, type ApiClientError } from "@/shared/api/errors";
import type { TokenStore } from "@/shared/auth/tokenStore";
import type { TokenPair } from "@/shared/auth/types";

export type HttpRequestOptions = {
  method?: string;
  body?: unknown;
  /** When true, send body as-is (FormData) and do not set Content-Type. */
  formData?: boolean;
  plantCode?: string | null;
  auth?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

type HttpClientDeps = {
  tokenStore: TokenStore;
  onUnauthorized?: () => void;
  getPlantCode?: () => string | null;
};

type RefreshOutcome =
  | { kind: "success"; pair: TokenPair }
  | { kind: "rejected" }
  | { kind: "unavailable" };

let refreshInFlight: Promise<RefreshOutcome> | null = null;

function newCorrelationId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `corr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: text };
  }
}

export function createHttpClient(deps: HttpClientDeps) {
  const baseUrl = getApiBaseUrl();

  function rejectSession(): RefreshOutcome {
    deps.tokenStore.clear();
    deps.onUnauthorized?.();
    return { kind: "rejected" };
  }

  async function refreshOutcome(): Promise<RefreshOutcome> {
    if (refreshInFlight) return refreshInFlight;

    const p = (async (): Promise<RefreshOutcome> => {
      const current = deps.tokenStore.get();
      if (!current?.refreshToken) return rejectSession();

      let res: Response;
      try {
        res = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Correlation-Id": newCorrelationId(),
          },
          body: JSON.stringify({ refreshToken: current.refreshToken }),
        });
      } catch {
        // API đang restart khi deploy — giữ refresh token trong trình duyệt.
        return { kind: "unavailable" };
      }

      const body = await parseBody(res);
      if (res.status === 401) return rejectSession();
      if (!res.ok) return { kind: "unavailable" };

      const login = body as ApiLoginResult;
      if (!login?.accessToken || !login.refreshToken) return { kind: "unavailable" };

      const pair: TokenPair = {
        accessToken: login.accessToken,
        refreshToken: login.refreshToken,
        accessTokenExpiresAt: login.accessTokenExpiresAt,
      };
      deps.tokenStore.set(pair);
      return { kind: "success", pair };
    })().finally(() => {
      refreshInFlight = null;
    });

    refreshInFlight = p;
    return p;
  }

  async function refreshTokens(): Promise<TokenPair | null> {
    const outcome = await refreshOutcome();
    return outcome.kind === "success" ? outcome.pair : null;
  }

  async function recoverUnauthorized(
    res: Response,
    headers: Headers,
    doFetch: () => Promise<Response>,
    useAuth: boolean,
  ): Promise<{ res: Response; unavailable: boolean }> {
    if (!(res.status === 401 && useAuth)) return { res, unavailable: false };

    const outcome = await refreshOutcome();
    if (outcome.kind === "success") {
      headers.set("Authorization", `Bearer ${outcome.pair.accessToken}`);
      return { res: await doFetch(), unavailable: false };
    }
    if (outcome.kind === "unavailable") return { res, unavailable: true };
    return { res, unavailable: false };
  }

  async function request<T>(path: string, options: HttpRequestOptions = {}): Promise<T> {
    const headers = new Headers();
    headers.set("Accept", "application/json");
    headers.set("X-Correlation-Id", newCorrelationId());

    const plant = options.plantCode ?? deps.getPlantCode?.() ?? null;
    if (plant) headers.set("X-Plant-Code", plant);

    if (options.body !== undefined && !options.formData) {
      headers.set("Content-Type", "application/json");
    }

    const useAuth = options.auth !== false;
    const tokens = deps.tokenStore.get();
    if (useAuth && tokens?.accessToken) {
      headers.set("Authorization", `Bearer ${tokens.accessToken}`);
    }

    if (options.headers) {
      for (const [k, v] of Object.entries(options.headers)) {
        if (v !== undefined && v !== "") headers.set(k, v);
      }
    }

    const doFetch = () =>
      fetch(`${baseUrl}${path}`, {
        method: options.method ?? (options.body !== undefined ? "POST" : "GET"),
        headers,
        body:
          options.body === undefined
            ? undefined
            : options.formData
              ? (options.body as BodyInit)
              : JSON.stringify(options.body),
        signal: options.signal,
      });

    let res = await doFetch();
    const recovered = await recoverUnauthorized(res, headers, doFetch, useAuth);
    if (recovered.unavailable) {
      throw normalizeApiError(503, {
        errorCode: "service_unavailable",
        message: "Máy chủ tạm thời không phản hồi.",
      });
    }
    res = recovered.res;

    const body = await parseBody(res);

    if (!res.ok) {
      const err = normalizeApiError(res.status, body);
      if (res.status === 401) {
        deps.tokenStore.clear();
        deps.onUnauthorized?.();
      }
      throw err;
    }

    return body as T;
  }

  async function requestBlob(path: string, options: HttpRequestOptions = {}): Promise<Blob> {
    const headers = new Headers();
    headers.set("Accept", "*/*");
    headers.set("X-Correlation-Id", newCorrelationId());

    const plant = options.plantCode ?? deps.getPlantCode?.() ?? null;
    if (plant) headers.set("X-Plant-Code", plant);

    const useAuth = options.auth !== false;
    const tokens = deps.tokenStore.get();
    if (useAuth && tokens?.accessToken) {
      headers.set("Authorization", `Bearer ${tokens.accessToken}`);
    }

    if (nativeHttpAvailable()) {
      const method = options.method ?? "GET";
      const load = () => requestNativeBlob(`${baseUrl}${path}`, method, headers);
      let native = await load();
      if (native.status === 401 && useAuth) {
        const outcome = await refreshOutcome();
        if (outcome.kind === "unavailable") {
          throw normalizeApiError(503, {
            errorCode: "service_unavailable",
            message: "Máy chủ tạm thời không phản hồi.",
          });
        }
        if (outcome.kind === "success") {
          headers.set("Authorization", `Bearer ${outcome.pair.accessToken}`);
          native = await load();
        }
      }
      if (native.status < 200 || native.status >= 300) {
        let body: unknown = null;
        if (native.errorText) {
          try {
            body = JSON.parse(native.errorText) as unknown;
          } catch {
            body = { message: native.errorText };
          }
        }
        const err = normalizeApiError(native.status, body);
        if (native.status === 401) {
          deps.tokenStore.clear();
          deps.onUnauthorized?.();
        }
        throw err;
      }
      return native.blob;
    }

    const doFetch = () =>
      fetch(`${baseUrl}${path}`, {
        method: options.method ?? "GET",
        headers,
        signal: options.signal,
      });

    let res = await doFetch();
    const recovered = await recoverUnauthorized(res, headers, doFetch, useAuth);
    if (recovered.unavailable) {
      throw normalizeApiError(503, {
        errorCode: "service_unavailable",
        message: "Máy chủ tạm thời không phản hồi.",
      });
    }
    res = recovered.res;

    if (!res.ok) {
      const body = await parseBody(res);
      const err = normalizeApiError(res.status, body);
      if (res.status === 401) {
        deps.tokenStore.clear();
        deps.onUnauthorized?.();
      }
      throw err;
    }

    return res.blob();
  }

  return {
    request,
    requestBlob,
    refreshTokens,
    async login(username: string, password: string, plantCode?: string | null) {
      return request<ApiLoginResult>("/api/v1/auth/login", {
        method: "POST",
        auth: false,
        body: { username, password, plantCode: plantCode ?? null },
      });
    },
    async me() {
      return request<ApiUserProfile>("/api/v1/auth/me");
    },
    async updateMyProfile(body: ApiUpdateMyProfileRequest) {
      return request<ApiUserProfile>("/api/v1/auth/me", {
        method: "PATCH",
        body,
      });
    },
    async changeMyPassword(body: ApiChangeMyPasswordRequest) {
      return request<{ status: string }>("/api/v1/auth/me/password", {
        method: "POST",
        body,
      });
    },
    async logout() {
      const current = deps.tokenStore.get();
      if (!current?.refreshToken) {
        deps.tokenStore.clear();
        return;
      }
      try {
        await request<{ status: string }>("/api/v1/auth/logout", {
          method: "POST",
          auth: false,
          body: { refreshToken: current.refreshToken },
        });
      } catch (e) {
        const err = e as ApiClientError;
        if (err.status !== 401) {
          // still clear local session
        }
      } finally {
        deps.tokenStore.clear();
      }
    },
  };
}

export type HttpClient = ReturnType<typeof createHttpClient>;

export type { ApiErrorBody };
